import { Alert, alpha, Box, Button, Divider, FormHelperText, InputAdornment, Stack, Tooltip, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import TelegramIcon from "@mui/icons-material/Telegram";
import GoogleIcon from "@mui/icons-material/Google";
import AppTextField from "components/input-fields/AppTextField";
import AuthMethodSwitcher from "components/auth/AuthMethodSwitcher";
import { useFormik } from "formik";
import { FC, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AuthenticationLayout from "page-sections/authentication/AuthenticationLayout";
import { SocialIconButton } from "page-sections/authentication/StyledComponents";
import useAuth from "../../hooks/useAuth";
import { AuthMethod } from "../../request/UserRequest";
import { PHONE_LOCALE } from "../../utils/phoneLocale";

const Login: FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<AuthMethod>("phone");

  const validationSchema = useMemo(() => {
    if (method === "email") {
      return Yup.object().shape({
        username: Yup.string().email("Некорректный email").required("Введите email"),
        password: Yup.string().required("Введите пароль"),
      });
    }
    if (method === "telegram") {
      return Yup.object().shape({
        username: Yup.string()
          .matches(/^[a-z0-9_]{5,32}$/, "Некорректный username (5–32 символа: a–z, 0–9, _)")
          .required("Введите Telegram username"),
        password: Yup.string().required("Введите пароль"),
      });
    }
    return Yup.object().shape({
      username: Yup.string()
        .max(PHONE_LOCALE.digits, "Неверный номер телефона")
        .min(PHONE_LOCALE.digits, "Неверный номер телефона")
        .required("Введите номер телефона"),
      password: Yup.string().required("Введите пароль"),
    });
  }, [method]);

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setErrors,
  } = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setInfoMessage("");
      try {
        await login(values.username, values.password, method);
        toast.success("Вход выполнен");
        navigate("/dashboard");
      } catch (err: any) {
        if (err?.code === "account_inactive") setInfoMessage(err.message);
        else if (err?.error !== undefined) setError(err.error);
        else if (err?.username) setError(err.username.join(", "));
        else setError(err?.message || "Не удалось войти");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleMethodChange = (next: string) => {
    if (next === method) return;
    setMethod(next as AuthMethod);
    setError("");
    setInfoMessage("");
    setFieldValue("username", "");
    setFieldTouched("username", false);
    setErrors({});
  };

  // Telegram: принимаем ввод с @, убираем его и приводим к нижнему регистру.
  const handleTelegramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("username", e.target.value.replace(/@/g, "").toLowerCase());
  };

  const identifierField = useMemo(() => {
    if (method === "email") {
      return (
        <AppTextField
          fullWidth
          label="Email"
          placeholder="you@example.com"
          name="username"
          type="email"
          inputMode="email"
          value={values.username}
          onBlur={handleBlur}
          onChange={handleChange}
          error={Boolean(touched.username && errors.username)}
          helperText={touched.username && errors.username}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AlternateEmailIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      );
    }

    if (method === "telegram") {
      return (
        <AppTextField
          fullWidth
          label="Telegram username"
          placeholder="username"
          name="username"
          type="text"
          value={values.username}
          onBlur={handleBlur}
          onChange={handleTelegramChange}
          error={Boolean(touched.username && errors.username)}
          helperText={touched.username && errors.username}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: "text.secondary", fontWeight: 500 }}>
                @
              </InputAdornment>
            ),
          }}
        />
      );
    }

    return (
      <AppTextField
        fullWidth
        label="Номер телефона"
        placeholder={PHONE_LOCALE.placeholder}
        name="username"
        type="tel"
        inputMode="numeric"
        value={values.username}
        onBlur={handleBlur}
        onChange={handleChange}
        error={Boolean(touched.username && errors.username)}
        helperText={touched.username && errors.username}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ color: "text.secondary", fontWeight: 500 }}>
              {PHONE_LOCALE.prefix}
            </InputAdornment>
          ),
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, values.username, errors.username, touched.username]);

  return (
    <AuthenticationLayout
      title="Вход в кабинет"
      subtitle="Выберите способ входа и введите пароль"
      route="/register"
      description="Ещё нет аккаунта?"
      routeName="Зарегистрироваться"
    >
      <form noValidate onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          <AuthMethodSwitcher
            value={method}
            onChange={handleMethodChange}
            methods={[
              { key: "phone", label: "Телефон", icon: <PhoneIphoneIcon /> },
              { key: "email", label: "Email", icon: <AlternateEmailIcon /> },
              { key: "telegram", label: "Telegram", icon: <TelegramIcon /> },
            ]}
          />

          {identifierField}

          <AppTextField
            fullWidth
            label="Пароль"
            name="password"
            type="password"
            value={values.password}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          {error && (
            <FormHelperText error sx={{ fontSize: 13, fontWeight: 500, textAlign: "center" }}>
              {error}
            </FormHelperText>
          )}

          {infoMessage && <Alert severity="info">{infoMessage}</Alert>}

          <Box sx={{ textAlign: "right" }}>
            <Link
              to="/forget-password"
              style={{ fontSize: 13, fontWeight: 500, color: "inherit" }}
            >
              Забыли пароль?
            </Link>
          </Box>

          {loading ? (
            <LoadingButton loading fullWidth size="large" variant="contained">
              Войти
            </LoadingButton>
          ) : (
            <Button fullWidth size="large" type="submit" variant="contained">
              Войти
            </Button>
          )}

          {/* Задел под соц-вход. OAuth пока не подключён — кнопки задизейблены. */}
          <Divider sx={{ my: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Или войдите через
            </Typography>
          </Divider>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
            <Tooltip title="Скоро">
              <span>
                <SocialIconButton
                  disabled
                  aria-label="Войти через Google"
                  sx={{ width: 48, minWidth: 48, height: 48, borderRadius: "10px" }}
                >
                  <GoogleIcon sx={{ fontSize: 20 }} />
                </SocialIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Скоро">
              <span>
                <SocialIconButton
                  disabled
                  aria-label="Войти через Telegram"
                  sx={{
                    width: 48,
                    minWidth: 48,
                    height: 48,
                    borderRadius: "10px",
                    color: (t) => alpha(t.palette.text.primary, 0.4),
                  }}
                >
                  <TelegramIcon sx={{ fontSize: 20 }} />
                </SocialIconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </form>
    </AuthenticationLayout>
  );
};

export default Login;
